import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Loader2, ArrowRightLeft, Gauge } from "lucide-react";
import { toast } from "sonner";

const label = (s) => s.replaceAll("_", " ");

export function ModuleTable({ moduleKey }) {
  const { can } = useAuth();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ data: {}, project_id: "", assigned_to: "" });

  const load = () => api.get(`/modules/${moduleKey}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    setMeta(null);
    api.get(`/modules/${moduleKey}/meta`).then((r) => {
      setMeta(r.data);
      if (r.data.project) api.get("/projects").then((x) => setProjects(x.data)).catch(() => {});
      if (r.data.assignee) api.get("/team").then((x) => setTeam(x.data)).catch(() => {});
    }).catch(() => {});
    load();
  }, [moduleKey]); // eslint-disable-line

  const resource = meta?.resource;
  const canCreate = resource && can(`${resource}.create`);
  const canEdit = resource && can(`${resource}.edit`);

  const setData = (name, value) => setForm((f) => ({ ...f, data: { ...f.data, [name]: value } }));

  const submit = async (e) => {
    e.preventDefault();
    if (moduleKey === "leads" && (form.data.phone || form.data.email)) {
      try {
        const { data } = await api.get("/leads/duplicate-check", { params: { phone: form.data.phone || "", email: form.data.email || "" } });
        if (data.matches.length > 0) {
          const names = data.matches.map((m) => `${m.type}: ${m.name} (${m.code})`).join("\n");
          if (!window.confirm(`Possible duplicate found:\n${names}\n\nCreate a new lead anyway?`)) return;
        }
      } catch { /* non-blocking */ }
    }
    setSaving(true);
    try {
      await api.post(`/modules/${moduleKey}`, { data: form.data, project_id: form.project_id || null, assigned_to: form.assigned_to || null });
      toast.success(`${meta.label} record created`);
      setOpen(false); setForm({ data: {}, project_id: "", assigned_to: "" }); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    setSaving(false);
  };

  const changeStatus = async (row, status) => {
    let data;
    if (moduleKey === "leads" && status === "LOST") {
      const reason = window.prompt("Reason for marking this lead as Lost:");
      if (!reason) return;
      data = { lost_reason: reason };
    }
    try { await api.put(`/modules/${moduleKey}/${row.id}`, data ? { status, data } : { status }); load(); toast.success(`Moved to ${label(status)}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const qualify = async (row) => {
    try {
      const { data } = await api.post(`/modules/leads/${row.id}/qualify`);
      toast.success(`Score ${data.qualification_score}/100 · ${data.qualification_status}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addFollowUp = async (row) => {
    const when = window.prompt("Follow-up due date (YYYY-MM-DD):", new Date(Date.now() + 86400000).toISOString().slice(0, 10));
    if (!when) return;
    try { await api.post("/followups", { lead_id: row.id, due_at: `${when}T09:00:00+05:30`, follow_up_type: "Call" }); toast.success("Follow-up scheduled"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convert = async (row) => {
    try { const { data } = await api.post(`/modules/leads/${row.id}/convert`); toast.success(`Converted → ${data.project_code}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const columns = meta?.columns || [];
  const fieldByName = useMemo(() => Object.fromEntries((meta?.fields || []).map((f) => [f.name, f])), [meta]);

  const cellValue = (row, name) => {
    const f = fieldByName[name];
    const v = row.data?.[name];
    if (v == null || v === "") return "—";
    if (f?.type === "currency") return inr(Number(v));
    return String(v);
  };

  if (!meta) return <div className="text-sm text-neutral-400 py-6">Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-neutral-500">{rows.length} {meta.label.toLowerCase()}</p>
        {canCreate && (
          <button onClick={() => setOpen(true)} data-testid={`module-${moduleKey}-new-button`} className="flex items-center gap-2 rounded-md bg-[#F5691C] hover:bg-[#D65A10] text-white text-sm font-medium px-4 py-2 transition-colors shadow-sm">
            <Plus size={16} /> New
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg border border-[#D7C6A9] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 bg-[#FafaF8]">
              <th className="px-5 py-3 font-medium">Code</th>
              {columns.map((c) => <th key={c} className="px-5 py-3 font-medium">{fieldByName[c]?.label || c}</th>)}
              {meta.project && <th className="px-5 py-3 font-medium">Project</th>}
              {meta.assignee && <th className="px-5 py-3 font-medium">Owner</th>}
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`module-${moduleKey}-row-${r.code}`} className="border-t border-[#EEE9DE] hover:bg-[#EEE9DE] transition-colors">
                <td className="px-5 py-3.5 mono text-neutral-700">{r.code}</td>
                {columns.map((c) => <td key={c} className="px-5 py-3.5 text-neutral-800">{cellValue(r, c)}</td>)}
                {meta.project && <td className="px-5 py-3.5 mono text-xs text-neutral-500">{r.project_code || "—"}</td>}
                {meta.assignee && <td className="px-5 py-3.5 text-neutral-600">{r.assigned_to_name || "—"}</td>}
                <td className="px-5 py-3.5">
                  {canEdit ? (
                    <select value={r.status} onChange={(e) => changeStatus(r, e.target.value)} data-testid={`module-${moduleKey}-status-${r.code}`} className="text-xs rounded-md border border-[#D7C6A9] bg-white px-2 py-1 outline-none focus:ring-2 focus:ring-[#F5691C]">
                      {meta.statuses.map((s) => <option key={s} value={s}>{label(s)}</option>)}
                    </select>
                  ) : <StatusBadge status={r.status} />}
                </td>
                <td className="px-5 py-3.5 text-right">
                  {moduleKey === "leads" && (
                    <>
                      <button onClick={() => qualify(r)} data-testid={`module-leads-qualify-${r.code}`} className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-[#F5691C] mr-3">
                        <Gauge size={14} /> {r.data?.qualification_score != null ? `${r.data.qualification_score}/100` : "Qualify"}
                      </button>
                      <button onClick={() => addFollowUp(r)} data-testid={`module-leads-followup-${r.code}`} className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-[#F5691C] mr-3">＋FU</button>
                    </>
                  )}
                  {meta.convertible && !r.converted_project_id && can("customers.create") && (
                    <button onClick={() => convert(r)} data-testid={`module-leads-convert-${r.code}`} className="inline-flex items-center gap-1.5 text-sm text-[#F5691C] hover:underline">
                      <ArrowRightLeft size={14} /> Convert
                    </button>
                  )}
                  {r.converted_project_id && <span className="text-xs text-neutral-400">Converted</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + 4} className="px-5 py-12 text-center text-sm text-neutral-400">No {meta.label.toLowerCase()} yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">New {meta.label}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {meta.fields.map((f) => (
              <div key={f.name}>
                <label className="text-xs font-medium text-neutral-600 uppercase tracking-wider">{f.label}{f.required && " *"}</label>
                {f.type === "textarea" ? (
                  <textarea data-testid={`module-field-${f.name}`} required={f.required} rows={2} value={form.data[f.name] || ""} onChange={(e) => setData(f.name, e.target.value)} className="mt-1.5 w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F5691C]" />
                ) : f.type === "select" ? (
                  <select data-testid={`module-field-${f.name}`} required={f.required} value={form.data[f.name] || ""} onChange={(e) => setData(f.name, e.target.value)} className="mt-1.5 w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F5691C]">
                    <option value="">Select…</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input data-testid={`module-field-${f.name}`} required={f.required} type={f.type === "currency" || f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={form.data[f.name] || ""} onChange={(e) => setData(f.name, e.target.value)} className="mt-1.5 w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F5691C]" />
                )}
              </div>
            ))}
            {meta.project && (
              <div><label className="text-xs font-medium text-neutral-600 uppercase tracking-wider">Project</label>
                <select data-testid="module-field-project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="mt-1.5 w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F5691C]">
                  <option value="">— None —</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.project_code} · {p.project_name}</option>)}
                </select></div>
            )}
            {meta.assignee && (
              <div><label className="text-xs font-medium text-neutral-600 uppercase tracking-wider">Owner</label>
                <select data-testid="module-field-assignee" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="mt-1.5 w-full rounded-md border border-[#D7C6A9] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#F5691C]">
                  <option value="">— Unassigned —</option>{team.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.role}</option>)}
                </select></div>
            )}
            <button data-testid={`module-${moduleKey}-submit`} disabled={saving} className="w-full flex items-center justify-center gap-2 rounded-md bg-[#F5691C] hover:bg-[#D65A10] text-white text-sm font-medium py-2.5 transition-colors disabled:opacity-60">
              {saving && <Loader2 size={16} className="animate-spin" />} Create
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
